import React, { useState, useEffect } from 'react';
import { situacaoService } from '../../services/entitiesServices';
import type { Situacao } from '../../types/entities';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useNotifier } from '../ui/Notifier';

interface SituacaoTableProps {
  onEdit?: (situacao: Situacao) => void;
  refreshKey?: number;
  onDeleted?: (id: string) => void;
}

export const SituacaoTable: React.FC<SituacaoTableProps> = ({ onEdit, refreshKey, onDeleted }) => {
  const [items, setItems] = useState<Situacao[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined);
  const [totalRecords, setTotalRecords] = useState<number | undefined>(undefined);
  const [allItems, setAllItems] = useState<Situacao[] | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const notifier = useNotifier();

  const load = async (params?: { page?: number; limit?: number; search?: string; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
    setLoading(true);
    try {
      const p = params?.page || page;
      const l = params?.limit || limit;
      const q = (params?.search || '').trim();
      const sb = params?.sortBy || sortBy;
      const so = params?.sortOrder || sortOrder;

      const query: any = { page: p, limit: l };
      if (q) query.q = q;
      if (sb) query.sortBy = sb;
      if (so) query.sortOrder = so;

      const resp: any = q ? await situacaoService.search(String(q), query) : await situacaoService.list(query);
      let data = Array.isArray(resp.data) ? resp.data : Array.isArray(resp) ? resp : (resp as any).situacoes || [];
      const pagination = resp?.pagination;

      // Ensure client-side sorting by requested field in case backend doesn't apply sort
      const sortField = sb;
      const sortDirection = so === 'desc' ? -1 : 1;
      if (sortField) {
        try {
          data = [...data].sort((a: any, b: any) => {
            const va = a?.[sortField];
            const vb = b?.[sortField];

            if (va === undefined || va === null) return 1 * sortDirection;
            if (vb === undefined || vb === null) return -1 * sortDirection;

            if (typeof va === 'number' && typeof vb === 'number') {
              return (va - vb) * sortDirection;
            }

            const sa = String(va).toLowerCase();
            const sbStr = String(vb).toLowerCase();
            if (sa === sbStr) return 0;
            return sa < sbStr ? -1 * sortDirection : 1 * sortDirection;
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('Client-side sort failed', e);
        }
      }

      const total = pagination?.total ?? (Array.isArray(data) ? data.length : undefined);
      setTotalRecords(total);

      const requestedLimit = params?.limit ?? limit;
      if (total && requestedLimit && requestedLimit >= total) {
        setAllItems(data);
        const pages = Math.ceil(total / limit);
        setTotalPages(pages || undefined);
        setPage(1);
        const start = 0;
        const end = Math.min(limit, data.length);
        setItems(data.slice(start, end));
      } else {
        setAllItems(undefined);
        setItems(data);
        setTotalPages(pagination?.totalPages || undefined);
        setPage(pagination?.page || p);
      }
    } catch (e) {
      console.error('Erro ao carregar situações', e);
      setItems([]);
      setTotalPages(undefined);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load({ page: 1, limit }); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { if (refreshKey) load({ page, limit, search: searchTerm, sortBy, sortOrder }); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [refreshKey]);

  const handleSort = (field: string) => {
    (async () => {
      if (field === sortBy) {
        const next = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortOrder(next);
        setSortBy(field);
        setPage(1);

        if (totalRecords && totalRecords > limit) {
          await load({ page: 1, limit: totalRecords, search: searchTerm, sortBy: field, sortOrder: next });
        } else {
          await load({ page: 1, limit, search: searchTerm, sortBy: field, sortOrder: next });
        }
      } else {
        setSortBy(field);
        setSortOrder('asc');
        setPage(1);

        if (totalRecords && totalRecords > limit) {
          await load({ page: 1, limit: totalRecords, search: searchTerm, sortBy: field, sortOrder: 'asc' });
        } else {
          await load({ page: 1, limit, search: searchTerm, sortBy: field, sortOrder: 'asc' });
        }
      }
    })();
  };

  const renderSortIcon = (field: string) => {
    if (sortBy !== field) {
      return (
        <svg className="inline-block w-3 h-3 ml-1 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M6 9l6-6 6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M6 15l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (sortOrder === 'asc') {
      return (
        <svg className="inline-block w-3 h-3 ml-1 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M6 15l6-6 6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    return (
      <svg className="inline-block w-3 h-3 ml-1 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M6 9l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  const handleSearch = (e?: React.FormEvent) => { if (e) e.preventDefault(); setPage(1); load({ page: 1, limit, search: searchTerm, sortBy, sortOrder }); };
  const handlePageChange = (newPage: number) => { setPage(newPage); load({ page: newPage, limit, search: searchTerm, sortBy, sortOrder }); };

  const handlePageChangeClient = (newPage: number) => {
    if (!allItems || !totalRecords) {
      handlePageChange(newPage);
      return;
    }
    const start = (newPage - 1) * limit;
    const end = start + limit;
    setItems(allItems.slice(start, end));
    setPage(newPage);
  };

  const handleDelete = (id: string) => {
    setDeleteTargetId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setConfirmOpen(false);
    try {
      await situacaoService.delete(deleteTargetId);
      notifier.show({ message: 'Situação excluída com sucesso', severity: 'success' });
      await load({ page, limit, search: searchTerm, sortBy, sortOrder });
      onDeleted?.(deleteTargetId);
    } catch (e: any) {
      notifier.show({ message: e.message || 'Erro ao excluir situação', severity: 'error' });
    } finally {
      setDeleteTargetId(null);
    }
  };

  if (loading) return <div className="p-3 text-center text-xs">Carregando situações...</div>;

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-xs font-medium" style={{ color: '#3d2b56' }}>Situações ({items.length})</h2>

        <form onSubmit={handleSearch} className="flex gap-1">
          <input type="text" placeholder="Buscar situações..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs" style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212', minWidth: '160px' }} />
          <button type="submit" className="px-2 py-1 text-xs font-medium text-white rounded-md" style={{ backgroundColor: '#7A61B2' }}>Buscar</button>
        </form>
      </div>

      {items.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th onClick={() => handleSort('nome')} className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer">Nome {renderSortIcon('nome')}</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((it, i) => (
                  <tr key={it.id} className={`hover:bg-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900">{it.nome || '-'}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-right text-xs font-medium">
                      <div className="flex justify-end space-x-1">
                        <button onClick={() => onEdit && onEdit(it)} className="text-green-600 hover:text-green-800 p-0.5" title="Editar">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                        </button>
                        <button onClick={() => handleDelete(it.id)} className="text-red-600 hover:text-red-800 p-0.5" title="Excluir">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-8"><p className="text-xs" style={{ color: '#9E9E9E' }}>{searchTerm ? 'Nenhuma situação encontrada para a busca.' : 'Nenhuma situação cadastrada.'}</p></div>
      )}

      {!loading && items.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-xs" style={{ color: '#9E9E9E' }}>{totalPages ? `Página ${page} de ${totalPages}` : `Mostrando ${items.length} situação(ões)`}</div>
          <div className="flex space-x-1">
            <button onClick={() => handlePageChangeClient(Math.max(1, page - 1))} disabled={page <= 1} className="px-2 py-1 text-xs border rounded disabled:opacity-50" style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}>Anterior</button>
            <span className="px-2 py-1 text-xs" style={{ color: '#3d2b56' }}>Página {page}{totalPages ? ` de ${totalPages}` : ''}</span>
            <button onClick={() => handlePageChangeClient(totalPages ? Math.min(totalPages, page + 1) : page + 1)} disabled={totalPages ? page >= totalPages : false} className="px-2 py-1 text-xs border rounded disabled:opacity-50" style={{ borderColor: '#C3B4DE', color: '#5d54a4' }}>Próxima</button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Excluir Situação"
        message="Tem certeza que deseja excluir esta situação?"
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="red"
        onConfirm={handleConfirmDelete}
        onCancel={() => { setConfirmOpen(false); setDeleteTargetId(null); }}
      />
    </div>
  );
};